import * as React from 'react';
import { useEffect, useState, useRef } from "react";
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";
import { Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import styles from "../SettingAccount/SettingAccountView.module.scss";
import {
  MISSION_FREQ_KEYS,
  MISSION_LABEL_KEYS,
  getMappedMissions,
} from "./missionHelpers";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const DialogPolicyPointView = (props) => {
  const {
    onWhatYouNeed,
    whatYouNeed,
    onRedeemPoints,
    redeemPoints,
    loading,
    missions,
    onGetRewardMissions,
    subTab,
    onResetRedeemPoints,
  } = props;
  
  const [typeMessage, setTypeMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(subTab || 'missions');
  const isRedeemingRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (subTab) {
      setActiveTab(subTab);
    }
  }, [subTab]);

  useEffect(() => {
    const params = {
      fields: "package,user",
    };
    if (!whatYouNeed.package) {
      onWhatYouNeed(params);
    } 
  }, []);

  useEffect(() => {
    onGetRewardMissions();
  }, []);

  useEffect(() => {
    return () => {
      if (onResetRedeemPoints) {
        onResetRedeemPoints();
      }
    };
  }, []);

  useEffect(() => {
    if (!isRedeemingRef.current) {
      return;
    }
    if (redeemPoints && redeemPoints.message) {
      setTypeMessage("error");
      isRedeemingRef.current = false;
    } else if (redeemPoints && redeemPoints.giftPoint) {
      setTypeMessage("success");
      isRedeemingRef.current = false;
      onGetRewardMissions({ force: true });
    }
  }, [redeemPoints]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setTypeMessage(null);
    if (onResetRedeemPoints) {
      onResetRedeemPoints();
    }
  };

  const handleRedeemPoints = (type) => {
    isRedeemingRef.current = true;
    const params = {
      type: `${type}`,
    };
    onRedeemPoints(params);
  };

  return (
    <>
      {typeMessage && (
        <Snackbar
          open={Boolean(typeMessage)}
          anchorOrigin={{
            horizontal: "center",
            vertical: "top",
          }}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={typeMessage}>
            {typeMessage === "error"
              ? t("account_management.tab_policy_point.exchangeError")
              : t("account_management.tab_policy_point.exchangeSuccess")}
          </Alert>
        </Snackbar>
      )}

      <div className={styles.cardHd}>
        <div className={styles.cardHdTitle}>{t('setting_account.rewards_program_title')}</div>
        <div className={styles.seggroup} id="rewardSegGroup">
          <button
            className={`${styles.segbtn} ${activeTab === 'missions' ? styles.active : ''}`}
            onClick={() => setActiveTab('missions')}
          >
            {t('setting_account.rewards_tab_missions')}
          </button>
          <button
            className={`${styles.segbtn} ${activeTab === 'redeem' ? styles.active : ''}`}
            onClick={() => setActiveTab('redeem')}
          >
            {t('setting_account.rewards_tab_redeem')}
          </button>
          <button
            className={`${styles.segbtn} ${activeTab === 'rules' ? styles.active : ''}`}
            onClick={() => setActiveTab('rules')}
          >
            {t('setting_account.rewards_tab_rules')}
          </button>
        </div>
      </div>

      {activeTab === 'missions' && (
        <div>
          {getMappedMissions(missions).map((mission) => {
            const isDone = mission.isCompleted === true;
            const freqKey = MISSION_FREQ_KEYS[mission.type];
            return (
              <div
                key={mission.id}
                className={`${styles.missionrow}${isDone ? ` ${styles.isdone}` : ''}`}
              >
                <div className={`${styles.missioncheck} ${isDone ? styles.done : styles.todo}`}>
                  {isDone && (
                    <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </div>
                <div className={styles.missionname}>
                  {t(MISSION_LABEL_KEYS[mission.id])}
                  {freqKey ? (
                    <span className={styles.missionfreq}>{t(freqKey)}</span>
                  ) : null}
                </div>
                <div className={styles.missionpts}>
                  {`+${Number(mission.points) || 0} ${t('setting_account.rewards_points_unit')}`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'redeem' && (
        <div className={styles.cardBd}>
          <div className={styles.rewardgrid}>
            <div className={styles.rewardcard}>
              <div className={styles.rewardemoji}>🎁</div>
              <div className={styles.rewardname}>{t('setting_account.redeem_1m_name')}</div>
              <div className={styles.rewardcost}>{t('setting_account.redeem_1m_cost')}</div>
              <button className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`} onClick={() => handleRedeemPoints(1)}>
                {t('setting_account.redeem_btn')}
              </button>
            </div>
            <div className={styles.rewardcard}>
              <div className={styles.rewardemoji}>🚀</div>
              <div className={styles.rewardname}>{t('setting_account.redeem_3m_name')}</div>
              <div className={styles.rewardcost}>{t('setting_account.redeem_3m_cost')}</div>
              <button className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`} onClick={() => handleRedeemPoints(3)}>
                {t('setting_account.redeem_btn')}
              </button>
            </div>
            <div className={styles.rewardcard}>
              <div className={styles.rewardemoji}>👑</div>
              <div className={styles.rewardname}>{t('setting_account.redeem_12m_name')}</div>
              <div className={styles.rewardcost}>{t('setting_account.redeem_12m_cost')}</div>
              <button className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`} onClick={() => handleRedeemPoints(12)}>
                {t('setting_account.redeem_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'rules' && (
        <div className={styles.cardBd}>
          <div className={styles.ruleslist}>
            <b>{t('setting_account.rules_scope_title')}</b> {t('setting_account.rules_scope_body')}
            <ul>
              <li dangerouslySetInnerHTML={{ __html: t('setting_account.rules_item_1') }} />
              <li dangerouslySetInnerHTML={{ __html: t('setting_account.rules_item_2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('setting_account.rules_item_3') }} />
              <li dangerouslySetInnerHTML={{ __html: t('setting_account.rules_item_4') }} />
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

DialogPolicyPointView.propTypes = {};
DialogPolicyPointView.defaultProps = {};
export default DialogPolicyPointView;